import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import api from '../../services/api';
import { logger } from '../../lib/logger';
import { CERT_SCENARIOS, extractTxId, getRecordType } from './constants';

const SUCCESS_KEYWORDS = ['uploaded successfully'];
const WARNING_KEYWORDS = ['processed with'];

const classifyProcessingMessage = (msg) => {
  const lower = (msg || '').toLowerCase();
  if (SUCCESS_KEYWORDS.some((k) => lower.includes(k))) return 'success';
  if (WARNING_KEYWORDS.some((k) => lower.includes(k))) return 'warning';
  return 'failed';
};

const collectErrors = (records) => {
  const errors = [];
  if (Array.isArray(records)) {
    records.forEach((rec) => {
      if (rec.ErrorCode && parseInt(rec.ErrorCode, 10) < 0 && parseInt(rec.ErrorCode, 10) !== -782) {
        errors.push({
          code: rec.ErrorCode,
          text: (rec.ErrorMessage || '').slice(0, 120),
        });
      }
    });
  }
  return errors;
};

export const useSandataCert = () => {
  const [apiStatus, setApiStatus] = useState(null);
  const [results, setResults] = useState({});
  const [statuses, setStatuses] = useState({});
  const [processingStatuses, setProcessingStatuses] = useState({});
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [runAllActive, setRunAllActive] = useState(false);
  const [runAllProgress, setRunAllProgress] = useState(null);
  const runAllAbort = useRef(false);

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await api.get('/sandata-cert/api-status');
      setApiStatus(data);
    } catch (e) {
      logger.error('Failed to fetch API status', e);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const checkProcessingStatus = async (scenario, result) => {
    const recordType = getRecordType(scenario.group);
    const txId = extractTxId(result);
    if (!txId) return;

    setStatuses((prev) => ({ ...prev, [scenario.id]: 'checking' }));
    try {
      const { data } = await api.get(
        `/sandata-cert/status/${txId}?record_type=${recordType}`
      );
      const msg = data?.data?.messageSummary || '';
      const errors = collectErrors(data?.data?.data || []);
      const status = classifyProcessingMessage(msg);

      setProcessingStatuses((prev) => ({
        ...prev,
        [scenario.id]: { status, message: msg, errors },
      }));
      setStatuses((prev) => ({ ...prev, [scenario.id]: status }));

      if (status === 'success') toast.success(`${scenario.label}: Processed successfully`);
      else if (status === 'warning') toast.info(`${scenario.label}: Processed with warnings`);
      else toast.error(`${scenario.label}: Processing failed`);
    } catch (e) {
      logger.error('Scenario processing error', e);
      setStatuses((prev) => ({ ...prev, [scenario.id]: 'warning' }));
    }
  };

  const runScenario = async (scenario) => {
    setStatuses((prev) => ({ ...prev, [scenario.id]: 'running' }));
    setSelectedScenario(scenario.id);
    setProcessingStatuses((prev) => ({ ...prev, [scenario.id]: null }));
    try {
      const { data } = await api.post(scenario.endpoint);
      setResults((prev) => ({ ...prev, [scenario.id]: data }));
      const allOk =
        data.total !== undefined ? data.success === data.total : data.result?.success;
      setStatuses((prev) => ({ ...prev, [scenario.id]: allOk ? 'success' : 'failed' }));
      if (allOk) toast.success(`${scenario.label}: Accepted`);
      else toast.error(`${scenario.label}: Some records failed`);
      return allOk;
    } catch (err) {
      setStatuses((prev) => ({ ...prev, [scenario.id]: 'failed' }));
      setResults((prev) => ({
        ...prev,
        [scenario.id]: { error: err.response?.data?.detail || err.message },
      }));
      toast.error(`${scenario.label}: ${err.response?.data?.detail || 'Failed'}`);
      return false;
    }
  };

  const runAll = async () => {
    setRunAllActive(true);
    runAllAbort.current = false;
    let completed = 0;

    for (const scenario of CERT_SCENARIOS) {
      if (runAllAbort.current) break;
      setRunAllProgress({
        current: completed + 1,
        total: CERT_SCENARIOS.length,
        label: scenario.label,
      });
       
      await runScenario(scenario);
      completed++;
    }

    setRunAllProgress(null);
    setRunAllActive(false);

    if (!runAllAbort.current) {
      toast.success('All certification tests completed');
    }
  };

  const stopAll = () => {
    runAllAbort.current = true;
    toast.info('Stopping after current test completes...');
  };

  const downloadJson = async () => {
    toast.info('Generating JSON export...');
    try {
      const { data } = await api.get('/sandata-cert/download-json');
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sandata_cert_${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Export downloaded');
    } catch (err) {
      toast.error('Export failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  return {
    apiStatus,
    results,
    statuses,
    processingStatuses,
    selectedScenario,
    setSelectedScenario,
    runAllActive,
    runAllProgress,
    runScenario,
    checkProcessingStatus,
    runAll,
    stopAll,
    downloadJson,
  };
};
